import { useState, useEffect } from 'react';
import { usersAPI, customersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/layout/Sidebar';
import toast from 'react-hot-toast';

const ROLES = ['admin','manager','vendor','viewer'];

// El valor del rol en la base se queda como 'vendor': esta amarrado en los
// permisos del backend (middleware vendorUp/managerUp) y en las consultas.
// Lo unico que cambia es lo que lee la gente en pantalla.
const ROLE_LABELS = {
  admin:   'Admin',
  manager: 'Manager',
  vendor:  'Sales Rep',
  viewer:  'Viewer',
};

// Contrasena temporal legible. Sin I, O, l, 0 ni 1: son las que la gente
// confunde al copiarlas de un correo. Dieciseis caracteres con al menos uno de
// cada clase, de crypto y no de Math.random, que es predecible.
function generaTemporal() {
  const MAY = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const MIN = 'abcdefghijkmnopqrstuvwxyz';
  const NUM = '23456789';
  const SIM = '!#$%&*+=?';
  const TODO = MAY + MIN + NUM + SIM;
  const azar = (n) => { const b = new Uint32Array(n); crypto.getRandomValues(b); return b; };

  const g = azar(4);
  const chars = [
    MAY[g[0] % MAY.length], MIN[g[1] % MIN.length],
    NUM[g[2] % NUM.length], SIM[g[3] % SIM.length],
  ];
  const resto = azar(12);
  for (let i = 0; i < 12; i++) chars.push(TODO[resto[i] % TODO.length]);

  const ord = azar(chars.length);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = ord[i] % (i + 1);
    const t = chars[i]; chars[i] = chars[j]; chars[j] = t;
  }
  return chars.join('');
}

export default function AdminUsersPage() {
  const { user: yo } = useAuth();
  const [users,     setUsers]    = useState([]);
  const [customers, setCustomers]= useState([]);
  const [loading,   setLoading]  = useState(true);
  const [showModal, setModal]    = useState(false);
  const [editing,   setEditing]  = useState(null);
  const [showAssign,setAssign]   = useState(null); // user to assign customers
  const [asig,      setAsig]     = useState(null); // foto de las asignaciones
  const [verAsig,   setVerAsig]  = useState(false);
  const [temporal,  setTemporal] = useState(null); // {user, password} recien generada
  const [ocupado,   setOcupado]  = useState(null); // id en proceso

  const load = async () => {
    setLoading(true);
    try {
      // active:'all' a proposito. Sin eso el modal solo veia los clientes
      // con customers.active en verdadero, que son 549 de 1,340, y esa
      // bandera no viene de Odoo: los 1,339 ligados estan vivos alla. El
      // panel se niega a esconder a quien facturo en 12 meses, asi que el
      // modal que sirve para arreglarlo tiene que poder alcanzarlo. Goebel
      // Kristi, 10,408 dolares y factura del 8 de octubre, estaba apagada
      // y no se podia ni seleccionar.
      const [u, c] = await Promise.all([
        usersAPI.list(), customersAPI.list({ active: 'all' })]);
      setUsers(u.data);
      setCustomers(c.data);
      // Aparte y tolerante a proposito: si el endpoint nuevo falla, la pantalla
      // de usuarios tiene que seguir sirviendo. Una funcion nueva no debe poder
      // tumbar una que ya funcionaba.
      try {
        const a = await customersAPI.assignments();
        // Se valida la FORMA, no nada mas que no truene. Un 200 con un cuerpo
        // que no es el JSON esperado deja asig en un valor truthy, y entonces
        // el candado de "sin foto no se asigna" falla ABIERTO: no sale el aviso
        // rojo, el boton queda habilitado, todos los clientes se pintan "sin
        // asignar" y el confirm no menciona ni una transferencia ni un cambio
        // de origen, mientras el backend si los escribe.
        // No es hipotetico: la ruta vive en routes/index.js, y si el front sale
        // publicado y la ruta no, un catch-all de la SPA contesta index.html
        // con 200 para /api/assignments.
        setAsig(a.data && Array.isArray(a.data.vigentes) ? a.data : null);
      } catch { setAsig(null); }
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const roleBadge = (role) => {
    const map = { admin:'badge-red', manager:'badge-orange', vendor:'badge-blue', viewer:'badge-gray' };
    return <span className={`badge ${map[role]||'badge-gray'}`}>{ROLE_LABELS[role] || role}</span>;
  };

  // La temporal se genera aqui y se manda por el mismo endpoint de cambio. Como
  // no es cambio propio, el backend la marca como temporal solo. Se muestra una
  // vez: no queda guardada en ningun lado en claro.
  const resetear = async (u) => {
    const ok = window.confirm(
      'Generar una contrasena temporal para ' + u.name + '?\n\n' +
      'La que tiene hoy deja de funcionar de inmediato, y tendra que cambiarla ' +
      'al entrar. Solo se muestra una vez.'
    );
    if (!ok) return;
    setOcupado(u.id);
    const temp = generaTemporal();
    try {
      await usersAPI.changePassword(u.id, { new_password: temp });
      setTemporal({ user: u, password: temp });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo resetear');
    } finally { setOcupado(null); }
  };

  const alternarAcceso = async (u) => {
    const apagar = u.active;
    const ok = window.confirm(
      (apagar ? 'Quitarle el acceso a ' : 'Devolverle el acceso a ') + u.name + '?\n\n' +
      (apagar
        ? 'Deja de entrar y su sesion actual muere en la siguiente pantalla que toque. ' +
          'Sus cotizaciones y comisiones no se tocan.'
        : 'Podra entrar con la contrasena que tenga. Si no la recuerda, generale una temporal.')
    );
    if (!ok) return;
    setOcupado(u.id);
    try {
      await usersAPI.update(u.id, {
        name: u.name, email: u.email, role: u.role, active: !u.active,
      });
      toast.success(apagar ? 'Acceso retirado' : 'Acceso restablecido');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo cambiar');
    } finally { setOcupado(null); }
  };

  const conCartera = {};
  if (asig && Array.isArray(asig.vigentes))
    for (const a of asig.vigentes) conCartera[String(a.user_id)] = true;

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-content">
        <div className="page-header">
          <div><h2>Users &amp; Settings</h2></div>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setModal(true); }}>
            + New User
          </button>
        </div>

        <div className="page-body">
          <div className="card" style={{ padding: 0 }}>
            {loading ? <div className="loading-center"><div className="spinner" /></div> : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Role</th><th>Status</th>
                      <th>Password</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const esYo = yo && String(u.id) === String(yo.id);
                      const trabajando = ocupado === u.id;
                      return (
                      <tr key={u.id} style={{ opacity: u.active ? 1 : 0.55 }}>
                        <td style={{ color: 'var(--text)', fontWeight: 600 }}>
                          {u.name}{esYo ? ' (usted)' : ''}
                        </td>
                        <td className="text-muted">{u.email}</td>
                        <td>{roleBadge(u.role)}</td>
                        <td>
                          {u.active
                            ? <span className="badge badge-green">Active</span>
                            : <span className="badge badge-gray">Inactive</span>}
                        </td>
                        <td>
                          {u.must_change_password
                            ? <span className="badge badge-orange">Temporal</span>
                            : <span className="badge badge-gray">Propia</span>}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <button className="btn btn-ghost btn-sm" disabled={trabajando}
                              onClick={() => { setEditing(u); setModal(true); }}>Edit</button>
                            {u.active && (u.role === 'vendor' || conCartera[String(u.id)]) && (
                              <button className="btn btn-ghost btn-sm" disabled={trabajando}
                                onClick={() => setAssign(u)}>Assign Customers</button>
                            )}
                            {!esYo && (
                              <button className="btn btn-ghost btn-sm" disabled={trabajando}
                                onClick={() => resetear(u)}>Reset Password</button>
                            )}
                            {!esYo && (
                              <button className="btn btn-ghost btn-sm" disabled={trabajando}
                                style={{ color: u.active ? 'var(--red)' : 'var(--green)' }}
                                onClick={() => alternarAcceso(u)}>
                                {u.active ? 'Deactivate' : 'Activate'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="text-muted text-sm" style={{ marginTop: 12, lineHeight: 1.6 }}>
            Temporal quiere decir que la contrasena la puso un admin y la persona
            todavia no la ha cambiado: al entrar no puede usar nada hasta que lo
            haga. Propia quiere decir que ya la cambio y nadie mas la conoce.
          </p>

          <AsignacionesPanel
            asig={asig}
            usuarios={users}
            abierto={verAsig}
            onToggle={() => setVerAsig(v => !v)}
          />
        </div>
      </div>

      {showModal && (
        <UserModal
          user={editing}
          esYo={yo && editing && String(editing.id) === String(yo.id)}
          onClose={() => { setModal(false); setEditing(null); }}
          onSaved={() => { setModal(false); setEditing(null); load(); }}
        />
      )}

      {showAssign && (
        <AssignModal
          user={showAssign}
          allCustomers={customers}
          asig={asig}
          onClose={() => setAssign(null)}
          onSaved={() => { setAssign(null); load(); }}
        />
      )}

      {temporal && (
        <TemporalModal info={temporal} onClose={() => setTemporal(null)} />
      )}
    </div>
  );
}

// -- CONTRASENA TEMPORAL RECIEN GENERADA ------------------------------
function TemporalModal({ info, onClose }) {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(info.password);
      setCopiado(true);
    } catch { toast.error('No se pudo copiar, seleccionela a mano'); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Contrasena temporal de {info.user.name}</span>
        </div>

        <p className="text-sm" style={{ lineHeight: 1.6, color: 'var(--text-2)' }}>
          Copiela ahora y mandesela. No se guarda en ningun lado y al cerrar esta
          ventana ya no hay forma de volver a verla; si se pierde, genere otra.
        </p>

        <div style={{
          marginTop: 14, padding: '14px 16px', borderRadius: 8,
          background: 'var(--bg)', border: '1px solid var(--border)',
          fontFamily: 'monospace', fontSize: 18, letterSpacing: 1,
          color: 'var(--text)', wordBreak: 'break-all', textAlign: 'center',
        }}>
          {info.password}
        </div>

        <p className="text-muted text-sm" style={{ marginTop: 12, lineHeight: 1.6 }}>
          Al entrar con ella, {info.user.name.split(' ')[0]} no va a poder usar el
          sistema hasta elegir una propia.
        </p>

        <div className="flex justify-between" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={copiar}>
            {copiado ? 'Copiada' : 'Copiar'}
          </button>
          <button className="btn btn-primary" onClick={onClose}>Listo</button>
        </div>
      </div>
    </div>
  );
}

// -- USER FORM MODAL -------------------------------------------------
function UserModal({ user, esYo, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:     user?.name     || '',
    email:    user?.email    || '',
    password: '',
    role:     user?.role     || 'vendor',
    active:   user?.active   ?? true,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.email) return toast.error('Name and email required');
    if (!user && !form.password)   return toast.error('Password required for new user');
    setSaving(true);
    try {
      if (user) {
        await usersAPI.update(user.id, { name: form.name, email: form.email, role: form.role, active: form.active });
        if (form.password) {
          await usersAPI.changePassword(user.id, { new_password: form.password });
        }
        toast.success('User updated');
      } else {
        await usersAPI.create(form);
        toast.success('User created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{user ? 'Edit User' : 'New User'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>X</button>
        </div>

        <div className="form-group">
          <label className="form-label">Full Name *</label>
          <input className="form-input" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input className="form-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-select" value={form.role} onChange={e => set('role', e.target.value)}
                    disabled={esYo}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" value={form.active} onChange={e => set('active', e.target.value === 'true')}
                    disabled={esYo}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {/* En la cuenta propia no se ofrece poner contrasena aqui: el backend
            exige la actual para el cambio propio y este formulario no la pide,
            asi que rebotaria con un mensaje que no explica nada. */}
        {esYo ? (
          <p className="text-muted text-sm" style={{ lineHeight: 1.6 }}>
            Su propio rol, estado y contrasena no se cambian desde aqui. Para la
            contrasena use Cambiar contrasena; para el rol, pidaselo a otro admin.
          </p>
        ) : (
          <div className="form-group">
            <label className="form-label">
              {user ? 'New Password (leave blank to keep)' : 'Password *'}
            </label>
            <input className="form-input" type="password" placeholder="........"
              value={form.password} onChange={e => set('password', e.target.value)} />
            {user && (
              <p className="text-muted text-sm" style={{ marginTop: 6 }}>
                Queda marcada como temporal: tendra que cambiarla al entrar.
              </p>
            )}
          </div>
        )}

        <div className="flex justify-between" style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : user ? 'Update User' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// -- ORIGEN DE CUENTA ------------------------------------------------
// El dato que decide el dinero. Hasta el 2026-10-20 esta pantalla NUNCA lo
// mandaba, asi que el backend le ponia 'house' por omision y toda asignacion
// hecha desde aqui quedo como cuenta de la casa. Eso produjo, entre otros, el
// ajuste de +210.22 de BK Hudson en el corte del 19-sep al 2-oct.
//
// El porcentaje concreto vive en commission_rates, no aqui. Este texto es el
// UNICO lugar de la pantalla que menciona cifras, a proposito: si se repiten en
// varios lados, el dia que se mueva el plan de comisiones todos mienten a la
// vez. Para quien lo lea, lo que importa es que el origen cambia el pago.
const ORIGEN_PORQUE =
  'Decide el porcentaje que se paga. Hoy la diferencia solo le pega a Noe, que '
  + 'cobra mas en cuenta propia que en cuenta de la casa sobre la misma venta; '
  + 'para Sandra, Eugenio, Daren y Jose Luis es informativo. El porcentaje exacto '
  + 'sale de la tabla de tarifas, no de esta pantalla.';
const ORIGEN_ETIQUETA = {
  vendor_sourced: 'Propia del vendedor',
  house:          'De la casa',
};
const ORIGEN_AYUDA = {
  vendor_sourced: 'El vendedor trajo la cuenta.',
  house:          'La cuenta la trajo la empresa, o venia de otro vendedor.',
};
function badgeOrigen(o) {
  if (!o) return <span className="badge badge-orange">sin origen</span>;
  return o === 'vendor_sourced'
    ? <span className="badge badge-green">propia</span>
    : <span className="badge badge-gray">casa</span>;
}

// De varias asignaciones del mismo cliente, la que de verdad manda: la abierta,
// y a falta de abierta la de fecha de inicio mas reciente. Construir el mapa
// sobrescribiendo sin mas dejaba ganar a la ultima del ORDER BY por nombre de
// vendedor, asi que despues de mover una cuenta de Sandra a Noe la pantalla
// seguia diciendo "hoy de Sandra".
function mapaDueno(vigentes) {
  const m = {};
  for (const a of (vigentes || [])) {
    const k = String(a.customer_id);
    const prev = m[k];
    if (!prev) { m[k] = a; continue; }
    const mejor = (!a.hasta && prev.hasta) ||
                  (!a.hasta === !prev.hasta && String(a.desde) > String(prev.desde));
    if (mejor) m[k] = a;
  }
  return m;
}

// -- PANEL DE REVISION DE ASIGNACIONES -------------------------------
// Existe porque no habia forma de REVISAR: el modal solo dejaba agregar, y los
// errores se descubrian un corte despues, ya pagados. Lo primero que muestra es
// lo que cuesta dinero: los clientes sin asignacion y los que tienen dos.
// Numero que abre su propia lista. Lo que el resumen dice en agregado, esto lo
// convierte en los renglones que lo componen: la queja fue exactamente esa, que
// el panel decia 551 y la lista no se veia en ningun lado.
const dinero = (n) => (Number(n) || 0).toLocaleString('en-US',
  { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Fechas a la americana para lo que se LEE. No para comparar: el orden
// alfabetico de AAAA-MM-DD es el orden cronologico, y es con el que se decide
// el rojo de los 6 meses.
const usa = (s) => {
  const d = String(s || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d)
    ? d.slice(5, 7) + '/' + d.slice(8, 10) + '/' + d.slice(0, 4)
    : '-';
};

function Celda({ n, activo, onPick, que }) {
  if (!n) return <span className="text-muted">0</span>;
  return (
    <button type="button" onClick={onPick} aria-pressed={!!activo}
      aria-label={n + ' ' + (que || 'clientes') + (activo ? ', viendose' : '')}
      title={activo ? 'Pica otra vez para quitar el filtro' : 'Ver estos clientes'}
      style={{ border: 'none', font: 'inherit', cursor: 'pointer',
               padding: '2px 6px', borderRadius: 4,
               color: 'var(--text)', fontWeight: activo ? 700 : 600,
               background: activo ? 'var(--border)' : 'none',
               textDecoration: activo ? 'none' : 'underline',
               textUnderlineOffset: 3 }}>
      {n}
    </button>
  );
}

function AsignacionesPanel({ asig, usuarios, abierto, onToggle }) {
  // Que lista se esta viendo. null = ninguna, y a proposito: dibujar los 1,333
  // renglones en 14 bloques cada vez que se abre el panel es la razon por la que
  // la lista no se encontraba.
  const [sel, setSel] = useState(null);   // { k: clave o '*', origen: tipo o null }
  // Lo facturado por cliente, que viene de Odoo. Aparte del resto a proposito:
  // si Odoo no contesta, la lista de asignaciones tiene que seguir sirviendo.
  //
  // Un solo objeto, y CON la lista a la que pertenece. Antes eran cuatro estados
  // sueltos, y al pasar de una lista a otra los datos de la anterior seguian
  // puestos: los clientes de la lista nueva salian con guion, y el guion
  // significa "no hay con que saberlo" -- con el aviso de la lista ANTERIOR
  // encima diciendo "N de estos clientes no estan ligados a Odoo". Una
  // afirmacion concreta, confiada y falsa sobre los clientes en pantalla.
  const [datos, setDatos] = useState(null);
  const [filtro, setFiltro] = useState('');
  // Esconder lo que no puede generar un cobro. Prendido por omision: de las 537
  // propias de Noe, 271 son clientes apagados y 126 nunca compraron. Que esten
  // marcadas propia o casa no mueve un peso, porque no van a cobrar.
  const [soloUtiles, setSoloUtiles] = useState(true);

  // Lo que sigue tolera que asig sea nulo porque el aviso de "no se pudo leer"
  // va DESPUES del useEffect: un hook detras de un return condicional cambia el
  // numero de hooks entre renders, y React lanza en pleno render. Pasaba en cada
  // carga de la pagina: el panel se monta con asig nulo y al llegar la respuesta
  // aparecia un hook de mas.
  const vig = (asig && asig.vigentes) || [];
  const sin = (asig && asig.sin_asignar) || [];
  const amb = (asig && asig.ambiguas) || [];
  // Por CLIENTE, no por renglon: un cliente con dos duenos son dos renglones, y
  // el banner decia "2 clientes con mas de un dueno" cuando era uno. Es el
  // numero mas grande y mas rojo de la pantalla.
  const nAmb = (asig && asig.ambiguas_clientes !== undefined)
    ? asig.ambiguas_clientes
    : new Set(amb.map(x => String(x.customer_id))).size;

  const porVendedor = {};
  for (const u of (Array.isArray(usuarios) ? usuarios : [])) {
    if (!u.active) continue;                       // los de baja sin cartera no estorban
    porVendedor[u.name + '\u0000' + u.id] = { nombre: u.name, filas: [] };
  }
  for (const a of vig) {
    const k = a.vendedor + '\u0000' + a.user_id;   // por id, no solo por nombre
    if (!porVendedor[k]) porVendedor[k] = { nombre: a.vendedor, filas: [] };
    porVendedor[k].filas.push(a);
  }
  const nombreDe = (k) => {
    const g = porVendedor[k];
    return String((g && g.nombre) || '(sin nombre)');
  };
  const grupos = Object.keys(porVendedor).sort(
    (a, b) => nombreDe(a).localeCompare(nombreDe(b)));
  const porCartera = grupos.slice().sort((a, b) => {
    const d = porVendedor[b].filas.length - porVendedor[a].filas.length;
    return d || nombreDe(a).localeCompare(nombreDe(b));
  });
  const coincide = (t) => !filtro || String(t || '').toLowerCase().includes(filtro.toLowerCase());

  // Una seleccion que apunta a una clave que ya no esta no es una seleccion.
  const selVivo = (sel && (sel.k === '*' || porVendedor[sel.k])) ? sel : null;
  const activa = (k, o) => !!selVivo && selVivo.k === k && selVivo.origen === o;
  // Picarle a la celda que ya esta prendida la apaga. Sin eso no hay forma de
  // volver a no-ver-nada mas que recargando.
  const picar = (k, o) => setSel(activa(k, o) ? null : { k: k, origen: o });

  // El filtro de texto solo tambien abre lista, y busca en TODO: es como se
  // contesta "este cliente de quien es" sin saber de antemano de quien buscar.
  const hayFiltro = String(filtro).trim() !== '';
  const verLista = !!selVivo || hayFiltro;
  const clavesVer = !verLista ? []
    : (selVivo && selVivo.k !== '*' ? [selVivo.k] : porCartera);
  const queSeVe = !selVivo ? ''
    : (selVivo.k === '*' ? 'todos' : nombreDe(selVivo.k))
      + (selVivo.origen ? ', ' + ORIGEN_ETIQUETA[selVivo.origen].toLowerCase() : '');
  // Los ids que se van a pintar. Es la lista exacta por la que se le pregunta a
  // Odoo: no tiene sentido traer lo facturado de clientes que no estan en
  // pantalla.
  const idsVer = [];
  for (const k of clavesVer) {
    const g = porVendedor[k];
    if (!g) continue;
    for (const a of g.filas) {
      if (selVivo && selVivo.origen && a.account_origin !== selVivo.origen) continue;
      if (!(coincide(a.cliente) || coincide(a.vendedor))) continue;
      idsVer.push(a.customer_id);
    }
  }
  const claveIds = idsVer.join(',');

  // Se espera a que deje de escribir antes de ir a Odoo: sin esto, cada tecla
  // del filtro manda una peticion.
  useEffect(() => {
    if (!claveIds) { setDatos(null); return; }
    let vivo = true;
    const t = setTimeout(() => {
      // Se mandan ids UNICOS: un cliente con dos duenos sale en dos grupos y
      // contaria dos veces contra el tope del servidor.
      const unicos = Array.from(new Set(claveIds.split(',').map(Number)));
      // La llamada va DENTRO de la cadena de promesas. Si customersAPI.compras
      // no existiera -- porque api.js se publico sin su linea -- el TypeError
      // seria sincrono y fuera de la cadena: ni .catch correria, y la pantalla
      // se quedaria en "..." para siempre sin decir una palabra. Ese es el peor
      // modo de falla posible para esto.
      Promise.resolve()
        .then(() => customersAPI.compras(unicos))
        .then(r => {
          if (!vivo) return;
          const d = (r && r.data) || {};
          // Se valida la FORMA, y tambien que venga el corte de 6 meses: sin el,
          // el rojo de las cuentas dormidas no se prende para nadie, sin aviso,
          // y las columnas de dinero se ven perfectas. La senal desaparece en
          // silencio.
          if (!d.compras || typeof d.compras !== 'object' || !d.desde_6m) {
            setDatos({ para: claveIds, mapa: {}, desde6: null, nota: '', grave: true,
              aviso: 'Odoo contesto algo que no se pudo leer. No hay numeros de compras.' });
            return;
          }
          const partes = [];
          // "Odoo no la reconoce" seria una afirmacion mas fuerte que el dato:
          // la foto de ligas tiene hasta diez minutos, asi que un cliente
          // ligado hace un rato todavia no aparece en ella. Se dice lo que de
          // verdad se sabe.
          if (d.liga_rota)
            partes.push(d.liga_rota + ' con una liga que no aparecia en la ultima lectura de Odoo');
          if (d.sin_liga_a_odoo)
            partes.push(d.sin_liga_a_odoo + ' sin liga a Odoo');
          if (d.omitidos)
            partes.push(d.omitidos + ' documento(s) de Odoo que no se pudieron leer');
          if (d.ids_ignorados)
            partes.push(d.ids_ignorados + ' id(s) de cliente que el servidor rechazo');
          setDatos({
            para: claveIds, mapa: d.compras, desde6: d.desde_6m,
            edad: d.leido_hace_seg,
            grave: !!d.truncado,
            aviso: d.truncado
              ? 'Odoo entrego ' + d.documentos_leidos + ' documentos de '
                + d.documentos_en_odoo + ' que dice tener: los totales estan '
                + 'INCOMPLETOS. No decidas con estos numeros.'
              : '',
            nota: partes.length ? partes.join(' y ') + '. Esos salen con guion.' : ''
          });
        })
        .catch(() => {
          if (!vivo) return;
          setDatos({ para: claveIds, mapa: {}, desde6: null, nota: '', grave: true,
            aviso: 'No se pudo leer Odoo. La lista de abajo esta completa; las '
                 + 'columnas de compras no traen nada.' });
        });
    }, 400);
    return () => { vivo = false; clearTimeout(t); };
  }, [claveIds]);

  // Si no se pudo leer, se DICE. Antes devolvia null y entonces "todo en orden"
  // y "no se pudo revisar" se veian igual, que es nada. Va aqui, DESPUES de
  // todos los hooks.
  if (!asig) {
    return (
      <div className="card" style={{ marginTop: 16 }}>
        <strong style={{ color: 'var(--red)' }}>
          No se pudieron leer las asignaciones de clientes
        </strong>
        <p className="text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
          Esto no quiere decir que esten bien: quiere decir que no se sabe.
          Recargue la pagina. Si sigue igual, no asigne nada hasta revisarlo.
        </p>
      </div>
    );
  }

  // Los datos sirven solo si son de la lista que se esta viendo. De ahi se
  // deduce si esta cargando, en lugar de llevar una bandera aparte: una bandera
  // se puede quedar prendida para siempre -- y se quedaba, cuando la respuesta
  // de una lista abandonada nunca cerraba su propio finally.
  const listo = !!datos && datos.para === claveIds;

  // Puede cobrar: compro en los ultimos 12 meses, SIN IMPORTAR la bandera de
  // activo, o esta activo y tiene historia de compras. El dinero le gana a la
  // bandera a proposito: medido hoy, 791 clientes estan apagados en el Quoter y
  // los 1,339 estan vivos en Odoo, asi que la bandera no dice lo que parece.
  // Goebel Kristi facturo 10,408 dolares y esta apagada; filtrando por bandera
  // habria desaparecido de la revision donde mas importa.
  const puedeCobrar = (a, c) => {
    if (!c) return true;                       // sin dato no se esconde nada
    if (Number(c.ult12) > 0) return true;
    return a.cliente_activo !== false && !!c.ultima_fecha;
  };
  const cargandoC = !!claveIds && !listo;

  // Suma de lo facturado de lo que se esta viendo. Es el numero que contesta la
  // pregunta de fondo: no cuantas cuentas estan marcadas propias, sino cuanto
  // dinero representan. Se suma sobre clientes DISTINTOS, no sobre renglones.
  const totalVisto = (() => {
    if (!listo || datos.grave) return null;
    let t = 0, con = 0, falta = 0;
    const yaSumado = new Set();
    for (const id of new Set(idsVer)) {
      const c = datos.mapa[id];
      if (!c) { falta += 1; continue; }
      con += 1;
      // Por partner resuelto: la cubeta de un padre ya incluye lo de sus hijos.
      const llave = String(c.pid === undefined || c.pid === null ? 'c' + id : c.pid);
      if (yaSumado.has(llave)) continue;
      yaSumado.add(llave);
      t += Number(c.total) || 0;
    }
    return { t, con, falta, sumados: yaSumado.size };
  })();

  const cuantosVer = clavesVer.reduce((n, k) => {
    const g = porVendedor[k];
    if (!g) return n;
    return n + g.filas
      .filter(a => !selVivo || !selVivo.origen || a.account_origin === selVivo.origen)
      .filter(a => coincide(a.cliente) || coincide(a.vendedor)).length;
  }, 0);

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="flex justify-between items-center">
        <div>
          <strong style={{ color: 'var(--text)' }}>Asignaciones de clientes</strong>
          <span className="text-muted text-sm" style={{ marginLeft: 10 }}>
            {vig.length} vigentes
            {sin.length > 0 && ' \u00b7 ' + sin.length + ' sin asignar'}
            {nAmb > 0 && ' \u00b7 ' + nAmb + ' con dos duenos'}
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onToggle}>
          {abierto ? 'Ocultar' : 'Revisar'}
        </button>
      </div>

      {!abierto && (
        <p className="text-muted text-sm" style={{ marginTop: 8, lineHeight: 1.6 }}>
          El origen de cuenta de cada cliente decide el porcentaje que se paga.
          Conviene revisarlo antes de cerrar la catorcena, porque despues ya no se
          corrige aqui: va como ajuste en el corte siguiente.
        </p>
      )}

      {abierto && (
        <div style={{ marginTop: 14 }}>
          {amb.length > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 8,
                          background: 'rgba(220,38,38,0.08)', border: '1px solid var(--red)' }}>
              <strong style={{ color: 'var(--red)' }}>
                {nAmb} cliente(s) con mas de un dueno hoy
              </strong>
              <p className="text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
                El motor no puede adivinar cual vale: escoge entre las dos y marca
                el renglon. Se arregla volviendo a asignar el cliente al vendedor
                correcto, con el origen correcto.
              </p>
              <ul className="text-sm" style={{ marginTop: 8, paddingLeft: 18 }}>
                {amb.map(a => (
                  <li key={a.customer_id + '-' + a.user_id + '-' + a.desde} style={{ marginTop: 3 }}>
                    {a.cliente} &rarr; {a.vendedor} {badgeOrigen(a.account_origin)}
                    <span className="text-muted"> desde {a.desde}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {sin.length > 0 && (
            <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 8,
                          background: 'rgba(234,88,12,0.08)', border: '1px solid #ea580c' }}>
              <strong style={{ color: '#ea580c' }}>
                {sin.length} cliente(s) activos sin asignacion
              </strong>
              <p className="text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
                Sin asignacion el motor no sabe si la cuenta es propia o de la
                casa, y aplica la tarifa sin origen. Fue el ajuste de 271.14 de
                INV/2026/0535 en el corte del 19-sep al 2-oct.
              </p>
              <p className="text-sm" style={{ marginTop: 8, lineHeight: 1.7 }}>
                {sin.slice(0, 40)
                  .map(c => c.name + (c.active === false ? ' (apagado)' : ''))
                  .join(' \u00b7 ')}
                {sin.length > 40 && ' \u00b7 y ' + (sin.length - 40) + ' mas'}
              </p>
            </div>
          )}

          <p className="text-muted text-sm" style={{ marginBottom: 8 }}>
            Los numeros se pican: abren la lista de esos clientes.
          </p>

          <div className="table-wrap" style={{ marginBottom: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th style={{ textAlign: 'right' }}>Clientes</th>
                  <th style={{ textAlign: 'right' }}>Propias</th>
                  <th style={{ textAlign: 'right' }}>De la casa</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {porCartera.map(k => {
                  const g = porVendedor[k];
                  const pr = g.filas.filter(a => a.account_origin === 'vendor_sourced').length;
                  const ca = g.filas.filter(a => a.account_origin === 'house').length;
                  const sinOrigen = g.filas.length - pr - ca;
                  const clientes = new Set(g.filas.map(a => String(a.customer_id))).size;
                  const dobles = g.filas.length - clientes;
                  const fuera = g.filas.some(a => a.vendedor_activo === false);
                  return (
                    <tr key={'r-' + k} style={{ opacity: fuera ? 0.6 : 1 }}>
                      <td style={{ color: 'var(--text)', fontWeight: 600 }}>{nombreDe(k)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <Celda n={clientes} activo={activa(k, null)}
                          que={'clientes de ' + nombreDe(k)}
                          onPick={() => picar(k, null)} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <Celda n={pr} activo={activa(k, 'vendor_sourced')}
                          que={'cuentas propias de ' + nombreDe(k)}
                          onPick={() => picar(k, 'vendor_sourced')} /></td>
                      <td style={{ textAlign: 'right' }}>
                        <Celda n={ca} activo={activa(k, 'house')}
                          que={'cuentas de la casa de ' + nombreDe(k)}
                          onPick={() => picar(k, 'house')} /></td>
                      <td>
                        {fuera && <span className="badge badge-red">ya no esta</span>}
                        {sinOrigen > 0 &&
                          <span className="badge badge-orange" style={{ marginLeft: 6 }}>
                            {sinOrigen} sin origen
                          </span>}
                        {dobles > 0 &&
                          <span className="badge badge-red" style={{ marginLeft: 6 }}>
                            {dobles} renglon(es) de mas
                          </span>}
                      </td>
                    </tr>
                  );
                })}
                <tr>
                  <td style={{ fontWeight: 700, color: 'var(--text)' }}>Total</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {new Set(vig.map(a => String(a.customer_id))).size}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {vig.filter(a => a.account_origin === 'vendor_sourced').length}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>
                    {vig.filter(a => a.account_origin === 'house').length}</td>
                  <td>
                    {(() => {
                      const so = vig.length
                        - vig.filter(a => a.account_origin === 'vendor_sourced').length
                        - vig.filter(a => a.account_origin === 'house').length;
                      return so > 0
                        ? <span className="badge badge-orange">{so} sin origen</span>
                        : null;
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="text-muted text-sm" style={{ marginBottom: 12, lineHeight: 1.6 }}>
            Clientes cuenta clientes distintos. Propias y De la casa cuentan
            asignaciones, que es donde vive el origen: si no cuadran contra
            Clientes es que un cliente trae dos renglones abiertos con el mismo
            dueno, y sale marcado. Dos duenos distintos es otra cosa y sale en el
            aviso rojo de arriba.
            El resumen es siempre el total y no lo mueve el filtro.
            Un vendedor marcado como que ya no esta y con cartera es dinero
            esperando: si entra un cobro en una de esas cuentas, el motor resuelve
            el origen con su asignacion. Hay que pasarselas a quien las atiende.
          </p>

          <input className="form-input" placeholder="Filtrar por cliente o vendedor"
            value={filtro} onChange={e => setFiltro(e.target.value)}
            style={{ marginBottom: 8 }} />

          <label className="flex items-center gap-2 text-sm"
            style={{ marginBottom: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={soloUtiles}
              onChange={e => setSoloUtiles(e.target.checked)} />
            <span className="text-muted">
              Esconder lo que no puede cobrar: apagado y sin compras, o que nunca
              compro. Lo que facturo en los ultimos 12 meses se muestra siempre,
              aunque este apagado.
            </span>
          </label>

          {selVivo && (
            <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
              <span className="text-sm" style={{ color: 'var(--text)', fontWeight: 600 }}>
                Viendo: {queSeVe} ({cuantosVer} asignadas)
                {totalVisto && totalVisto.con > 0 &&
                  <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                    {dinero(totalVisto.t)} facturado en lo que se ve
                    {totalVisto.falta > 0 && ', ' + totalVisto.falta + ' sin dato'}
                    {totalVisto.sumados < totalVisto.con &&
                      ', ' + (totalVisto.con - totalVisto.sumados) + ' comparten cuenta en Odoo'}
                    {listo && datos.edad > 60 &&
                      ', leido hace ' + Math.round(datos.edad / 60) + ' min'}
                  </span>}
                {cargandoC && <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                  leyendo Odoo...
                </span>}
                {listo && datos.nota &&
                  <span className="text-muted" style={{ fontWeight: 400, marginLeft: 8 }}>
                    {datos.nota}
                  </span>}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setSel(null)}>
                Quitar
              </button>
            </div>
          )}

          {!verLista && (
            <p className="text-muted text-sm" style={{ lineHeight: 1.6 }}>
              Pica un numero de la tabla para ver esos clientes, o escribe un
              nombre aqui arriba para buscarlo entre todos.
            </p>
          )}

          {/* Cuando se busca por texto sin haber picado un numero no hay
              encabezado de "Viendo", y entonces el aviso de que esta leyendo
              Odoo no salia en ninguna parte: las celdas decian "..." y nada
              explicaba por que. */}
          {!selVivo && verLista && (cargandoC || (listo && datos.nota)) && (
            <p className="text-muted text-sm" style={{ marginBottom: 12 }}>
              {cargandoC ? 'leyendo Odoo...' : datos.nota}
            </p>
          )}

          {listo && datos.grave && datos.aviso && (
            <div style={{ marginBottom: 12, padding: '12px 14px', borderRadius: 8,
                          background: 'rgba(220,38,38,0.08)', border: '1px solid var(--red)' }}>
              <strong style={{ color: 'var(--red)' }}>
                Los numeros de compras no son confiables
              </strong>
              <p className="text-sm" style={{ marginTop: 6, lineHeight: 1.6 }}>
                {datos.aviso}
              </p>
            </div>
          )}

          {verLista && cuantosVer === 0 && (
            <p className="text-muted text-sm">
              Ningun cliente coincide. Los datos estan, nada mas que el filtro no
              deja pasar nada: quitale el filtro de texto o pica otro numero.
            </p>
          )}

          {clavesVer.map(k => {
            const g = porVendedor[k];
            if (!g) return null;
            let filas = g.filas
              .filter(a => !selVivo || !selVivo.origen || a.account_origin === selVivo.origen)
              .filter(a => coincide(a.cliente) || coincide(a.vendedor));
            const antesDeEsconder = filas.length;
            // El filtro y el orden solo pueden aplicarse cuando ya llegaron los
            // numeros de Odoo. Sin ellos la lista sale completa y alfabetica:
            // mas vale una lista larga que una lista recortada con un criterio
            // que todavia no se conoce.
            if (listo && !datos.grave) {
              if (soloUtiles)
                filas = filas.filter(a => puedeCobrar(a, datos.mapa[a.customer_id]));
              filas = filas.slice().sort((x, y) => {
                const cx = datos.mapa[x.customer_id] || {}, cy = datos.mapa[y.customer_id] || {};
                const d12 = (Number(cy.ult12) || 0) - (Number(cx.ult12) || 0);
                if (d12) return d12;
                const dt = (Number(cy.total) || 0) - (Number(cx.total) || 0);
                if (dt) return dt;
                return String(x.cliente || '').localeCompare(String(y.cliente || ''));
              });
            }
            const escondidos = antesDeEsconder - filas.length;
            if (!filas.length && !escondidos) return null;
            const inactivo = g.filas.some(a => a.vendedor_activo === false);
            return (
              <div key={k} style={{ marginBottom: 18 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  {g.nombre}
                  {inactivo && <span className="badge badge-red" style={{ marginLeft: 8 }}>ya no esta</span>}
                  <span className="text-muted text-sm" style={{ marginLeft: 8, fontWeight: 400 }}>
                    {filas.length}
                    {antesDeEsconder !== filas.length && ' de ' + antesDeEsconder}
                    {selVivo && selVivo.origen
                      ? ' ' + ORIGEN_ETIQUETA[selVivo.origen].toLowerCase()
                      : ' cliente(s)'}
                    {escondidos > 0 &&
                      ' \u00b7 ' + escondidos + ' sin posibilidad de cobro, escondidos'}
                  </span>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Cliente</th><th>Ciudad</th><th>Origen</th>
                        <th style={{ textAlign: 'right' }}>Compras</th>
                        <th style={{ textAlign: 'right' }}>12 meses</th>
                        <th>Ultima compra</th>
                        <th>Desde</th><th>Asigno</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filas.map(a => (
                        <tr key={a.user_id + '-' + a.customer_id + '-' + a.desde}>
                          <td style={{ color: 'var(--text)' }}>
                            {a.cliente || '(cliente borrado)'}
                            {a.cliente_activo === false &&
                              <span className="badge badge-gray" style={{ marginLeft: 6 }}>inactivo</span>}
                          </td>
                          <td className="text-muted">{a.city}</td>
                          <td>{badgeOrigen(a.account_origin)}</td>
                          {(() => {
                            const c = listo && !datos.grave
                              ? datos.mapa[a.customer_id] : undefined;
                            // Mientras carga, lo que no se tiene se dibuja
                            // cargando, no con guion. El guion quiere decir "no
                            // hay con que saberlo", y eso se lee como "este
                            // cliente no ha comprado": una respuesta, y falsa.
                            // Pasa al pasar de una lista a otra, con los
                            // clientes que no venian en la anterior.
                            // Cuando los numeros no son confiables se ponen
                            // signos de pregunta, no cifras con aviso en otra
                            // parte: una cifra con formato de dinero se lee como
                            // un hecho, y el aviso se va de la pantalla al
                            // primer scroll de una lista de 551 renglones.
                            if (listo && datos.grave) return (
                              <>
                                <td className="text-muted" style={{ textAlign: 'right' }}>?</td>
                                <td className="text-muted" style={{ textAlign: 'right' }}>?</td>
                                <td className="text-muted">?</td>
                              </>
                            );
                            if (cargandoC && !c) return (
                              <>
                                <td className="text-muted" style={{ textAlign: 'right' }}>...</td>
                                <td className="text-muted" style={{ textAlign: 'right' }}>...</td>
                                <td className="text-muted">...</td>
                              </>
                            );
                            // null quiere decir que no hay con que saberlo: sin
                            // liga a Odoo, o Odoo no contesto. Guion, no cero:
                            // un cero se lee como "no ha comprado" y es mentira.
                            if (!c) return (
                              <>
                                <td className="text-muted" style={{ textAlign: 'right' }}>-</td>
                                <td className="text-muted" style={{ textAlign: 'right' }}>-</td>
                                <td className="text-muted">-</td>
                              </>
                            );
                            // Dormida es una cuenta que COMPRO y dejo de
                            // comprar. Una que nunca ha comprado no es lo mismo:
                            // puede ser nueva y que nadie le haya vendido
                            // todavia. Pintar las dos de rojo juntaba la senal
                            // de reasignar con la de empezar a vender.
                            const viejo = !!datos.desde6 && !!c.ultima_fecha
                              && c.ultima_fecha < datos.desde6;
                            return (
                              <>
                                <td style={{ textAlign: 'right' }}>{dinero(c.total)}</td>
                                <td style={{ textAlign: 'right' }}>{dinero(c.ult12)}</td>
                                <td style={viejo ? { color: 'var(--red)', fontWeight: 600 } : undefined}
                                    title={viejo ? 'Sin comprar en mas de 6 meses' : ''}>
                                  {c.ultima_fecha
                                    ? usa(c.ultima_fecha)
                                    : <span className="text-muted">nunca</span>}
                                  {c.ultima_fecha &&
                                    <span className="text-muted" style={{ marginLeft: 6 }}>
                                      {dinero(c.ultimo_monto)}
                                    </span>}
                                </td>
                              </>
                            );
                          })()}
                          <td className="text-muted">{a.desde}</td>
                          <td className="text-muted">{a.asignado_por || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -- ASSIGN CUSTOMERS MODAL ------------------------------------------
function AssignModal({ user, allCustomers, asig, onClose, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [origen,   setOrigen]   = useState('');   // a proposito vacio
  const [nota,     setNota]     = useState('');
  const [filtro,   setFiltro]   = useState('');
  const [verSolo,  setVerSolo]  = useState('todos');
  const [saving,   setSaving]   = useState(false);

  const dueno = mapaDueno(asig && asig.vigentes);

  // TODAS las vigentes por cliente, no solo la que manda. En el caso ambiguo un
  // cliente puede tener dos duenos, y mapaDueno se queda con uno: el confirm
  // decia "1 se le quita a otro vendedor" cuando se le quitaba a dos.
  const vigPorCliente = {};
  for (const a of ((asig && asig.vigentes) || [])) {
    const k = String(a.customer_id);
    if (!vigPorCliente[k]) vigPorCliente[k] = [];
    vigPorCliente[k].push(a);
  }
  const vigentesDe = (id) => vigPorCliente[String(id)] || [];

  // Los duenos que existen hoy, para poder filtrar por ellos. Sin esto, para
  // encontrar las 65 cuentas de los dos vendedores que se fueron habia que
  // recorrer toda la lista a ojo cazando renglones naranjas.
  const duenos = [];
  for (const k of Object.keys(dueno)) {
    const d = dueno[k];
    if (String(d.user_id) === String(user.id)) continue;
    if (!duenos.some(x => String(x.user_id) === String(d.user_id)))
      duenos.push({ user_id: d.user_id, vendedor: d.vendedor });
  }
  duenos.sort((a, b) => String(a.vendedor).localeCompare(String(b.vendedor)));

  const toggle = (id) => setSelected(s =>
    s.includes(id) ? s.filter(x => x !== id) : [...s, id]
  );

  const lista = allCustomers.filter(c => {
    const d = dueno[String(c.id)];
    if (verSolo === 'suyos'  && !(d && String(d.user_id) === String(user.id))) return false;
    if (verSolo === 'libres' && d) return false;
    if (verSolo.indexOf('de:') === 0) {
      const uid = verSolo.slice(3);
      if (!(d && String(d.user_id) === String(uid))) return false;
    }
    if (!filtro) return true;
    const t = filtro.toLowerCase();
    return String(c.name || '').toLowerCase().includes(t)
        || String(c.city || '').toLowerCase().includes(t);
  });

  const nombreDe = (id) => {
    const c = allCustomers.find(x => String(x.id) === String(id));
    return c ? c.name : '#' + id;
  };

  // Las que le quitarian la cuenta a OTRO vendedor.
  const transferencias = selected
    .reduce((acc, id) => acc.concat(vigentesDe(id)), [])
    .filter(d => String(d.user_id) !== String(user.id));

  // Y las que CAMBIAN DE ORIGEN. Esto es lo que de verdad mueve dinero, y la
  // version anterior de esta pantalla no avisaba: con el filtro "solo los de
  // Noe", marcar todo y escoger "de la casa" convertia su cartera completa al
  // porcentaje bajo en un clic, sin una sola pantalla de confirmacion.
  const cambiosDeOrigen = !origen ? [] : selected
    .reduce((acc, id) => acc.concat(vigentesDe(id)), [])
    .filter(d => d.account_origin && d.account_origin !== origen);

  // Origen anomalo para ESE dueno. Mau tiene 429 cuentas de casa y cero propias;
  // asignarle una propia deberia gritar. Paso de verdad el 21-oct: Guerrero y
  // Messina se guardaron como propias cuando iban de casa, y la confirmacion no
  // ayudo porque "como cuenta propia del vendedor" se lee natural en cualquier
  // caso. Un aviso que sale solo cuando el dato es raro si se lee.
  const carteraDelDueno = (asig && Array.isArray(asig.vigentes))
    ? asig.vigentes.filter(v => String(v.user_id) === String(user.id)) : [];
  const delOrigen = origen
    ? carteraDelDueno.filter(v => v.account_origin === origen).length : 0;
  const anomalo = !!origen && carteraDelDueno.length >= 10 && delOrigen === 0;
  // Si el dueno tiene cuentas de los DOS origenes, el origen le cambia el
  // porcentaje. Importa porque el aviso de "origen raro" de arriba nunca puede
  // saltarle a Noe -- tiene de los dos tipos -- y Noe es justo la persona donde
  // el origen mueve dinero de verdad: 10 por ciento en propia contra 3 en casa.
  // Asignarle un cliente nuevo como de casa cuando iba como propia es el error
  // de BK Hudson, los 210.22 del corte del 19-sep al 2-oct.
  // Se dice como un hecho y no como alarma: le aplica a casi todos los
  // vendedores, y una alarma que siempre suena no se oye.
  const propiasDelDueno = carteraDelDueno.filter(v => v.account_origin === 'vendor_sourced').length;
  const casaDelDueno    = carteraDelDueno.filter(v => v.account_origin === 'house').length;
  const origenImporta   = propiasDelDueno > 0 && casaDelDueno > 0;

  const handleSave = async () => {
    // Sin la foto de quien tiene que cliente no se puede saber si marcar una
    // casilla le quita la cuenta a otro vendedor. Antes que transferir a ciegas,
    // no se deja asignar.
    if (!asig) return toast.error('No se pudieron leer las asignaciones actuales. Recargue la pagina.');
    if (!origen) return toast.error('Escoja el origen de cuenta');

    // El confirm sale SIEMPRE, no solo cuando hay transferencia, y lista por
    // nombre lo que se va a escribir. Dos razones: los marcados sobreviven al
    // cambio de filtro, asi que puede haber cuentas en la tanda que ya no se
    // ven en la lista; y un confirm que solo aparece en un caso se vuelve un
    // clic automatico, mientras que uno que cambia de texto se lee.
    const lineas = [];
    lineas.push(selected.length + ' cliente(s) a ' + user.name
      + ' como ' + ORIGEN_ETIQUETA[origen].toLowerCase() + ', a partir de hoy.');
    if (anomalo) {
      lineas.push('');
      lineas.push('OJO: ' + user.name + ' no tiene NI UNA cuenta '
        + ORIGEN_ETIQUETA[origen].toLowerCase() + ' entre sus '
        + carteraDelDueno.length + '. Esta seria la primera. Si se equivoco de'
        + ' origen, cancele y escoja el otro.');
    }
    if (origenImporta && !anomalo) {
      lineas.push('');
      lineas.push(user.name + ' tiene cuentas de los dos origenes ('
        + propiasDelDueno + ' propias y ' + casaDelDueno + ' de casa), asi que'
        + ' el origen le cambia el porcentaje. Confirme que '
        + ORIGEN_ETIQUETA[origen].toLowerCase() + ' es el correcto.');
    }
    if (cambiosDeOrigen.length) {
      lineas.push('');
      lineas.push('OJO: ' + cambiosDeOrigen.length + ' de esos cambian de origen, y el origen'
        + ' es lo que decide el porcentaje:');
      for (const d of cambiosDeOrigen.slice(0, 12))
        lineas.push('  - ' + d.cliente + ': ' + ORIGEN_ETIQUETA[d.account_origin].toLowerCase()
          + ' -> ' + ORIGEN_ETIQUETA[origen].toLowerCase());
      if (cambiosDeOrigen.length > 12) lineas.push('  - y ' + (cambiosDeOrigen.length - 12) + ' mas');
    }
    if (transferencias.length) {
      lineas.push('');
      lineas.push(transferencias.length + ' se le quitan a otro vendedor:');
      for (const d of transferencias.slice(0, 12))
        lineas.push('  - ' + d.cliente + ' (hoy de ' + d.vendedor + ')');
      if (transferencias.length > 12) lineas.push('  - y ' + (transferencias.length - 12) + ' mas');
      lineas.push('');
      lineas.push('El vendedor anterior deja de generar comision en esas cuentas a '
        + 'partir de hoy. Lo ya cobrado y pagado no se toca.');
    }
    lineas.push('');
    lineas.push('Continuar?');
    if (!window.confirm(lineas.join('\n'))) return;

    setSaving(true);
    try {
      // En serie y no en paralelo: el backend cierra la asignacion anterior y
      // abre la nueva dentro de una transaccion por cliente, y si dos corren
      // juntas contra el mismo cliente una puede pisar a la otra.
      // Se cuenta lo que SI se hizo: si truena a la mitad, hay que decir
      // cuantas quedaron, porque las que ya se escribieron no se deshacen.
      let hechas = 0;
      try {
        for (const cid of selected) {
          await customersAPI.assignVendor(cid, user.id, origen, nota || null);
          hechas++;
        }
      } catch (err) {
        if (hechas > 0)
          toast.error('Se alcanzaron a asignar ' + hechas + ' de ' + selected.length
            + ' antes de fallar. Revise el panel de asignaciones antes de reintentar.');
        throw err;
      }
      toast.success(hechas + ' cliente(s) asignados a ' + user.name
        + ' como ' + ORIGEN_ETIQUETA[origen].toLowerCase());
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'No se pudo asignar');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">Asignar clientes a {user.name}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>X</button>
        </div>

        {!asig && (
          <p className="text-sm" style={{ marginBottom: 12, lineHeight: 1.6, color: 'var(--red)' }}>
            No se pudieron leer las asignaciones actuales, asi que no se puede
            saber quien tiene cada cliente hoy. Recargue la pagina antes de
            asignar: marcar a ciegas podria transferirle una cuenta a otro
            vendedor sin que se vea.
          </p>
        )}

        <p className="text-muted text-sm" style={{ marginBottom: 14, lineHeight: 1.6 }}>
          Marcar un cliente que ya es de otro vendedor <strong>se lo transfiere</strong>,
          a partir de hoy. No se puede asignar con fecha anterior: una asignacion
          retroactiva cambiaria lo que daria volver a correr un corte ya pagado.
        </p>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Origen de cuenta *</label>
            <select className="form-select" value={origen} onChange={e => setOrigen(e.target.value)}>
              <option value="">Escoja uno...</option>
              <option value="vendor_sourced">{ORIGEN_ETIQUETA.vendor_sourced}</option>
              <option value="house">{ORIGEN_ETIQUETA.house}</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Motivo (opcional)</label>
            <input className="form-input" value={nota} maxLength={200}
              placeholder="Ej: reasignacion por salida de Cesar"
              onChange={e => setNota(e.target.value)} />
          </div>
        </div>
        <p className="text-muted text-sm" style={{ marginTop: -6, marginBottom: 12, lineHeight: 1.6 }}>
          {origen ? ORIGEN_AYUDA[origen] + ' ' : ''}{ORIGEN_PORQUE} Aplica a todos
          los que marque en esta tanda: si unos son propios y otros de casa,
          hagalo en dos tandas.
        </p>

        {selected.length > 0 && (
          <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 8,
                        background: 'var(--surface-2, rgba(127,127,127,0.08))',
                        border: '1px solid var(--border)' }}>
            <div className="text-sm" style={{ color: 'var(--text)', fontWeight: 600,
                                              marginBottom: 6 }}>
              {selected.length} marcado(s) para asignar
            </div>
            <div className="flex" style={{ flexWrap: 'wrap', gap: 6 }}>
              {selected.map(id => {
                const c = allCustomers.find(x => String(x.id) === String(id));
                return (
                  <span key={'m-' + id} className="badge badge-gray"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {c ? c.name : 'cliente ' + id}
                    <button type="button" onClick={() => toggle(id)}
                      aria-label={'Desmarcar ' + (c ? c.name : id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer',
                               padding: 0, font: 'inherit', color: 'inherit' }}>x</button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-2" style={{ marginBottom: 10 }}>
          <input className="form-input" placeholder="Filtrar por nombre o ciudad"
            value={filtro} onChange={e => setFiltro(e.target.value)} />
          <select className="form-select" value={verSolo} onChange={e => setVerSolo(e.target.value)}
                  style={{ maxWidth: 230 }}>
            <option value="todos">Todos los clientes</option>
            <option value="suyos">Solo los de {user.name.split(' ')[0]}</option>
            <option value="libres">Solo sin asignar</option>
            {duenos.map(d => (
              <option key={d.user_id} value={'de:' + d.user_id}>Solo los de {d.vendedor}</option>
            ))}
          </select>
        </div>

        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {lista.length === 0 && (
            <p className="text-muted text-sm" style={{ padding: '10px 4px' }}>
              Ningun cliente coincide con ese filtro.
            </p>
          )}
          {lista.map(c => {
            const d = dueno[String(c.id)];
            const deOtro = d && String(d.user_id) !== String(user.id);
            return (
              <label key={c.id} className="flex items-center gap-3"
                style={{ padding: '8px 4px', cursor: 'pointer',
                         borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                <span style={{ fontWeight: 600, minWidth: 190 }}>{c.name}</span>
                <span className="text-muted" style={{ minWidth: 90 }}>{c.city}</span>
                {d
                  ? <span style={{ color: deOtro ? '#ea580c' : undefined }}
                          className={deOtro ? '' : 'text-muted'}>
                      {deOtro ? 'hoy de ' + d.vendedor : 'ya es suyo'} {badgeOrigen(d.account_origin)}
                    </span>
                  : <span className="badge badge-orange">sin asignar</span>}
              </label>
            );
          })}
        </div>

        {origenImporta && !anomalo && (
          <p className="text-muted text-sm" style={{ marginTop: 10, lineHeight: 1.6 }}>
            {user.name} tiene {propiasDelDueno} cuenta(s) propia(s) y{' '}
            {casaDelDueno} de la casa, asi que a esta persona el origen le cambia
            el porcentaje de comision. No es un error: es para que lo escoja a
            proposito.
          </p>
        )}

        {anomalo && (
          <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8,
                        background: 'rgba(234,88,12,0.08)', border: '1px solid #ea580c' }}>
            <strong style={{ color: '#ea580c' }}>
              Origen raro para {user.name}
            </strong>
            <p className="text-sm" style={{ marginTop: 4, lineHeight: 1.6 }}>
              De sus {carteraDelDueno.length} cuentas, ninguna es{' '}
              {ORIGEN_ETIQUETA[origen].toLowerCase()}. Esta seria la primera.
              Revise que sea el origen que queria.
            </p>
          </div>
        )}

        {(transferencias.length > 0 || cambiosDeOrigen.length > 0) && (
          <div className="text-sm" style={{ marginTop: 12, lineHeight: 1.7, color: '#ea580c' }}>
            {cambiosDeOrigen.length > 0 && (
              <div>
                <strong>{cambiosDeOrigen.length} de los marcados cambian de origen</strong>
                {' '}y el origen es lo que decide el porcentaje.
              </div>
            )}
            {transferencias.length > 0 && (
              <div>
                {transferencias.length} de los marcados son de otro vendedor y se le
                van a transferir a {user.name}.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-between items-center" style={{ marginTop: 16 }}>
          <span className="text-muted text-sm">
            {selected.length} marcados
            {selected.length > 0 && lista.length < allCustomers.length &&
              ' (algunos pueden estar fuera del filtro: ' +
              selected.slice(0, 3).map(nombreDe).join(', ') +
              (selected.length > 3 ? '...' : '') + ')'}
          </span>
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={handleSave}
                    disabled={saving || !selected.length || !origen || !asig}>
              {saving ? 'Asignando...' : 'Asignar marcados'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
